<template>
  <div class="word-slim-main">
    <section class="word-slim-hero">
      <h2>Word 瘦身</h2>
      <p>压缩 docx 内嵌图片体积，保持段落、表格与图片数量不变，适合学术/报告提交前减小文件大小。</p>
    </section>

    <div class="word-slim-workspace">
      <div class="form-panel">
        <div class="panel-head">
          <h2>上传 Word 文档</h2>
          <p class="panel-tip">仅支持 docx，单文件不超过 20MB。处理通常在 1 分钟内完成。</p>
        </div>

        <div
            class="upload-zone"
            :class="{ dragover: dragOver, uploading: uploading, readonly: readonly }"
            @click="pickFile"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop">
          <input ref="fileInput" type="file" class="file-input" accept=".docx" :disabled="readonly || uploading" @change="onFileChange">
          <div v-if="uploading" class="upload-loading">
            <div class="loading-spinner"></div>
            <p class="upload-loading-title">正在上传文档…</p>
            <p v-if="uploadingFileName" class="upload-loading-name">{{ uploadingFileName }}</p>
          </div>
          <template v-else>
            <i class="bi bi-cloud-upload upload-icon"></i>
            <p class="upload-title">拖拽或 <span class="upload-link">点击上传</span> docx</p>
            <p class="upload-hint">自动压缩 JPEG/PNG 图片，跳过 EMF/WMF 公式图</p>
          </template>
        </div>

        <div v-if="uploadSession" class="upload-info">
          <div class="file-row">
            <i class="bi bi-file-earmark-word"></i>
            <div class="file-meta">
              <div class="file-name">{{ uploadSession.sourceFileName }}</div>
              <div class="file-sub">{{ uploadSession.fileSizeLabel }} · 预计消耗 {{ uploadSession.quotaCost || 0 }} 点</div>
            </div>
            <button type="button" class="link-btn" @click="removeUpload">移除</button>
          </div>
        </div>

        <div class="submit-row">
          <button type="button" class="primary-btn" :disabled="!canSubmit" @click="handleSubmit">
            {{ submitting ? '提交中…' : '开始瘦身' }}
          </button>
        </div>
        <p v-if="balanceInsufficient" class="warn-text">可用点数不足，请先充值</p>
        <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
      </div>

      <aside class="info-panel">
        <div class="notice-box">
          <div class="notice-head"><i class="bi bi-info-circle-fill"></i><span>使用说明</span></div>
          <p>系统会重采样 docx 包内的大尺寸 JPEG/PNG 图片（最大宽度 1920px），并在处理后校验文档结构。EMF/WMF 矢量图（常见于公式）将原样保留。</p>
        </div>
        <ul class="feature-list">
          <li v-for="item in features" :key="item.title">
            <i :class="item.icon"></i>
            <div>
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
            </div>
          </li>
        </ul>
      </aside>
    </div>

    <div class="table-panel">
      <div class="table-head">
        <h3>处理记录</h3>
        <button type="button" class="ghost-btn" :disabled="recordsLoading" @click="loadRecords">
          <i class="bi bi-arrow-clockwise" :class="{ spinning: recordsLoading }"></i>
        </button>
      </div>

      <div v-if="!readonly && records.length" class="record-table">
        <div v-for="item in records" :key="item.taskNo" class="table-row">
          <div class="item name">
            <div class="item-label">文件</div>
            <div class="item-value title-text">{{ item.sourceFileName || item.taskNo }}</div>
          </div>
          <div class="item size">
            <div class="item-label">体积</div>
            <div class="item-value">
              <span v-if="item.status === '2'">{{ item.sourceSizeLabel }} → {{ item.resultSizeLabel }}</span>
              <span v-else>{{ item.sourceSizeLabel || '-' }}</span>
            </div>
          </div>
          <div class="item saved">
            <div class="item-label">节省</div>
            <div class="item-value">{{ item.status === '2' ? (item.savedRatioLabel || '-') : '-' }}</div>
          </div>
          <div class="item status">
            <div class="item-label">状态</div>
            <div class="item-value">
              <span v-if="isProcessing(item)" class="tag tag-loading">处理中 {{ item.progress || 0 }}%</span>
              <span v-else-if="item.status === '2'" class="tag tag-done">已完成</span>
              <span v-else-if="item.status === '3'" class="tag tag-fail" :title="item.errorMsg">失败</span>
              <span v-else class="tag tag-queue">排队中</span>
            </div>
          </div>
          <div class="item date">
            <div class="item-label">提交时间</div>
            <div class="item-value">{{ item.createTime || '-' }}</div>
          </div>
          <div class="item operate">
            <div class="item-label">操作</div>
            <div class="item-value op-links">
              <button type="button" class="op-link" :disabled="item.status !== '2' || downloadingTaskNo === item.taskNo" @click="downloadFile(item)">
                <i class="bi bi-download"></i>{{ downloadingTaskNo === item.taskNo ? '获取中…' : '下载' }}
              </button>
              <button type="button" class="op-link danger" @click="deleteRecord(item)">
                <i class="bi bi-trash"></i>删除
              </button>
            </div>
          </div>
        </div>
      </div>

      <p v-else-if="readonly" class="empty-text">登录后查看处理记录</p>
      <p v-else-if="recordsLoading" class="empty-text">加载中…</p>
      <p v-else class="empty-text">暂无记录，上传 docx 后开始瘦身</p>
    </div>
  </div>
</template>

<script>
import {
  uploadWordSlimFile,
  deleteWordSlimUpload,
  submitWordSlim,
  getWordSlimTasks,
  getWordSlimDownloadUrl,
  deleteWordSlimRecord
} from '@/api/portal/wordSlim'

const POLL_INTERVAL = 3000

function buildRequestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'ws-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10)
}

export default {
  name: 'WordSlimView',
  props: {
    readonly: { type: Boolean, default: false },
    balance: { type: Number, default: 0 }
  },
  data() {
    return {
      dragOver: false,
      uploading: false,
      uploadingFileName: '',
      submitting: false,
      uploadSession: null,
      errorMessage: '',
      pollTimer: null,
      records: [],
      recordsLoading: false,
      downloadingTaskNo: '',
      features: [
        { icon: 'bi bi-images', title: '智能压缩', desc: '针对 word/media 内嵌图片重采样与压缩，显著减小体积。' },
        { icon: 'bi bi-shield-check', title: '结构校验', desc: '处理后校验段落、表格、图片数量与原文一致。' },
        { icon: 'bi bi-vector-pen', title: '保护公式', desc: 'EMF/WMF 矢量图（公式）默认跳过，避免破坏显示。' },
        { icon: 'bi bi-download', title: 'DOCX 下载', desc: '瘦身完成后下载 _slim.docx，可直接用于提交或继续编辑。' }
      ]
    }
  },
  computed: {
    canSubmit() {
      return !this.readonly && !this.submitting && !this.uploading && this.uploadSession && !this.balanceInsufficient
    },
    balanceInsufficient() {
      if (!this.uploadSession || !this.uploadSession.quotaCost) {
        return false
      }
      return this.balance < this.uploadSession.quotaCost
    }
  },
  mounted() {
    if (!this.readonly) {
      this.loadRecords()
      this.startPolling()
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    isProcessing(item) {
      return item && (item.status === '0' || item.status === '1')
    },
    hasActiveRecords() {
      return this.records.some(item => this.isProcessing(item))
    },
    pickFile() {
      if (this.readonly || this.uploading) {
        if (this.readonly) {
          this.$emit('require-login')
        }
        return
      }
      this.$refs.fileInput && this.$refs.fileInput.click()
    },
    onDragOver() {
      if (!this.readonly && !this.uploading) {
        this.dragOver = true
      }
    },
    onDragLeave() {
      this.dragOver = false
    },
    onDrop(e) {
      this.dragOver = false
      if (this.readonly || this.uploading) {
        if (this.readonly) {
          this.$emit('require-login')
        }
        return
      }
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]
      if (file) {
        this.uploadFile(file)
      }
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0]
      if (file) {
        this.uploadFile(file)
      }
      e.target.value = ''
    },
    uploadFile(file) {
      this.errorMessage = ''
      this.uploading = true
      this.uploadingFileName = file.name
      uploadWordSlimFile(file)
          .then(res => {
            this.uploadSession = res.data
          })
          .catch(err => {
            this.errorMessage = err.message || '上传失败'
          })
          .finally(() => {
            this.uploading = false
            this.uploadingFileName = ''
          })
    },
    removeUpload() {
      if (!this.uploadSession || !this.uploadSession.uploadId) {
        this.uploadSession = null
        return
      }
      deleteWordSlimUpload(this.uploadSession.uploadId)
          .then(() => {
            this.uploadSession = null
          })
          .catch(err => {
            this.errorMessage = err.message || '删除失败'
          })
    },
    handleSubmit() {
      if (this.readonly) {
        this.$emit('require-login')
        return
      }
      if (this.balanceInsufficient) {
        this.$emit('require-recharge')
        return
      }
      if (!this.canSubmit) {
        return
      }
      this.submitting = true
      this.errorMessage = ''
      submitWordSlim({
        uploadId: this.uploadSession.uploadId,
        requestId: buildRequestId()
      })
          .then(() => {
            this.uploadSession = null
            this.$emit('word-slim-submitted')
            this.loadRecords()
            this.startPolling()
          })
          .catch(err => {
            this.errorMessage = err.message || '提交失败'
          })
          .finally(() => {
            this.submitting = false
          })
    },
    loadRecords() {
      this.recordsLoading = true
      getWordSlimTasks({ page: 1, pageSize: 50 })
          .then(res => {
            this.records = res.data || []
            if (!this.hasActiveRecords()) {
              this.stopPolling()
            } else if (!this.pollTimer) {
              this.startPolling()
            }
          })
          .catch(() => {
            this.records = []
          })
          .finally(() => {
            this.recordsLoading = false
          })
    },
    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(() => {
        if (this.hasActiveRecords()) {
          this.loadRecords()
        } else {
          this.stopPolling()
        }
      }, POLL_INTERVAL)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    downloadFile(item) {
      if (!item || item.status !== '2') {
        return
      }
      this.downloadingTaskNo = item.taskNo
      getWordSlimDownloadUrl(item.taskNo)
          .then(res => {
            const url = res.data
            if (url) {
              window.open(url, '_blank')
            }
          })
          .catch(err => {
            this.errorMessage = err.message || '获取下载链接失败'
          })
          .finally(() => {
            this.downloadingTaskNo = ''
          })
    },
    deleteRecord(item) {
      if (!item || !item.taskNo) {
        return
      }
      if (!window.confirm('确定删除该记录吗？')) {
        return
      }
      deleteWordSlimRecord(item.taskNo)
          .then(() => this.loadRecords())
          .catch(err => {
            this.errorMessage = err.message || '删除失败'
          })
    }
  }
}
</script>

<style scoped>
.word-slim-main { display: flex; flex-direction: column; gap: 20px; }
.word-slim-hero { text-align: center; padding: 8px 16px 4px; }
.word-slim-hero h1 { margin: 0 0 10px; font-size: 28px; font-weight: 700; }
.word-slim-hero p { margin: 0 auto; max-width: 720px; font-size: 14px; line-height: 1.75; color: #6b7280; }
.word-slim-workspace { display: grid; grid-template-columns: minmax(0, 2fr) minmax(260px, 1fr); gap: 20px; align-items: stretch; }
.form-panel, .table-panel, .info-panel { border-radius: 14px; background: #fff; border: 1px solid #e5e7eb; }
.form-panel { display: flex; flex-direction: column; padding: 22px 24px; }
.panel-head h2 { margin: 0 0 8px; font-size: 18px; font-weight: 600; }
.panel-tip { margin: 0; font-size: 13px; color: #9ca3af; line-height: 1.6; }
.upload-zone { margin-top: 16px; padding: 32px 20px; border: 2px dashed #d1d5db; border-radius: 12px; background: #fafafa; text-align: center; cursor: pointer; }
.upload-zone.dragover, .upload-zone.uploading { border-color: #1976d2; background: #e3f2fd; }
.upload-zone.readonly { cursor: not-allowed; opacity: 0.7; }
.upload-zone.uploading { pointer-events: none; cursor: wait; }
.file-input { display: none; }
.upload-icon { font-size: 28px; color: #1976d2; }
.upload-title { margin: 10px 0 4px; font-size: 15px; }
.upload-link { color: #1976d2; font-weight: 500; }
.upload-hint { margin: 0; font-size: 12px; color: #9ca3af; }
.upload-loading { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.loading-spinner { width: 36px; height: 36px; border: 3px solid #e5e7eb; border-top-color: #1976d2; border-radius: 50%; animation: spin 0.8s linear infinite; }
.upload-loading-title { margin: 0; font-size: 15px; font-weight: 600; color: #1565c0; }
.upload-loading-name { margin: 0; font-size: 13px; color: #374151; }
.upload-info { margin-top: 14px; padding-top: 14px; border-top: 1px dashed #e5e7eb; }
.file-row { display: flex; align-items: center; gap: 12px; }
.file-row > i { font-size: 24px; color: #2563eb; }
.file-meta { flex: 1; min-width: 0; }
.file-name { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.file-sub { margin-top: 4px; font-size: 13px; color: #6b7280; }
.link-btn { border: none; background: none; color: #1976d2; cursor: pointer; }
.submit-row { margin-top: 16px; }
.primary-btn { padding: 10px 28px; border: none; border-radius: 999px; background: var(--primary, #1976d2); color: #fff; font-size: 14px; font-weight: 500; cursor: pointer; }
.primary-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.warn-text, .error-text { margin: 10px 0 0; font-size: 14px; color: #b91c1c; }
.info-panel { padding: 20px; }
.notice-box { padding: 14px 16px; border-radius: 10px; background: #eff6ff; border: 1px solid #bfdbfe; }
.notice-head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; font-size: 14px; font-weight: 600; color: #1d4ed8; }
.notice-box p { margin: 0; font-size: 13px; line-height: 1.7; color: #374151; }
.feature-list { list-style: none; margin: 16px 0 0; padding: 0; display: flex; flex-direction: column; gap: 14px; }
.feature-list li { display: flex; gap: 12px; }
.feature-list i { font-size: 20px; color: #1976d2; margin-top: 2px; }
.feature-list h3 { margin: 0 0 4px; font-size: 14px; font-weight: 600; }
.feature-list p { margin: 0; font-size: 13px; color: #6b7280; line-height: 1.6; }
.table-panel { padding: 18px 20px 22px; }
.table-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.table-head h3 { margin: 0; font-size: 16px; font-weight: 600; }
.ghost-btn { border: none; background: none; color: #6b7280; cursor: pointer; font-size: 18px; }
.ghost-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.spinning { animation: spin 0.8s linear infinite; }
.record-table { display: flex; flex-direction: column; gap: 10px; }
.table-row { display: grid; grid-template-columns: 1.4fr 1.2fr 0.7fr 0.9fr 1fr 1.1fr; gap: 10px; padding: 12px 14px; border-radius: 10px; background: #f9fafb; border: 1px solid #eef2f7; align-items: center; }
.item-label { font-size: 11px; color: #9ca3af; margin-bottom: 4px; }
.item-value { font-size: 13px; color: #111827; }
.title-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tag { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 12px; }
.tag-loading { background: #fef3c7; color: #b45309; }
.tag-done { background: #dcfce7; color: #15803d; }
.tag-fail { background: #fee2e2; color: #b91c1c; }
.tag-queue { background: #e5e7eb; color: #374151; }
.op-links { display: flex; flex-wrap: wrap; gap: 8px; }
.op-link { border: none; background: none; color: #1976d2; cursor: pointer; font-size: 12px; padding: 0; }
.op-link.danger { color: #dc2626; }
.op-link:disabled { opacity: 0.5; cursor: not-allowed; }
.empty-text { margin: 0; padding: 24px 0; text-align: center; font-size: 14px; color: #9ca3af; }
@keyframes spin { to { transform: rotate(360deg); } }
@media (max-width: 960px) {
  .word-slim-workspace { grid-template-columns: 1fr; }
  .table-row { grid-template-columns: 1fr 1fr; }
  .item.operate { grid-column: 1 / -1; }
}
</style>
